import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { logNewRelicPageAction } from '@makemydeal/dr-activities-common';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import { clearManualFieldOverrides } from '@makemydeal/dr-dash-store/actions/manualFieldOverridesActionCreators';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import { CDL_USER_INTENT } from '@makemydeal/dr-dash-store/constants';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import { NumericInput } from '@makemydeal/dr-shared-components/numeric-input';
import type { TextInputEventValue } from '@interstate/components/TextInput';
import { ToggleGroup } from '@interstate/components/ToggleGroup';
import { Box } from '@interstate/components/Box';
import { MessageBlock } from '@interstate/components/MessageBlock';

import type { IPaymentGridErrorState, StateTree } from '@makemydeal/dr-dash-types';
import type { ResidualItem, ResidualsFromTerms } from '@makemydeal/dr-offer-redux';
import { appliesToTerm } from '@makemydeal/dr-offer-redux';
import type { ResidualValueType } from '@makemydeal/dr-platform-types';
import { LEASE, RESIDUAL_DOLLAR, RESIDUAL_PERCENT } from '@makemydeal/dr-platform-types';
import {
    DOLLAR_SIGN,
    GENERIC_LAST_FIELD_CHANGED_ERROR,
    INPUT_INVALID_ERROR_MESSAGE,
    PAYMENT_GRID_REQUIRED_INFO_MESSAGE,
    PERCENT_SIGN
} from '../../constants';
import { areStringsEqual, ENTER_KEY_CODE, getNewTarget } from '../common/utils';
import { useIsAspPaymentGridRedesignActive } from './hooks/useIsAspPaymentGridRedesignActive';
import { useIsColumnCalculated } from './hooks/useIsColumnCalculated';
import { useIsExplicitLenderProgramGridActive } from './hooks/useIsExplicitLenderProgramGridActive';
import RowLabelCell from './RowLabelCell';
import { CompactGridItem, StyledResidualUnitToggle } from './styled.components';
import { disabledStyle, hiddenInputMessageStyle, requiredInfoStyle } from './utils';
import { PaymentGridOverrideIndicator } from './components/PaymentGridOverrideIndicator';
import { usePaymentGridOverrideIndicator } from './hooks/usePaymentGridOverrideIndicator';

const RESIDUAL_LABEL = 'Residual';
const ResidualTypeMap = {
    [RESIDUAL_DOLLAR]: DOLLAR_SIGN,
    [RESIDUAL_PERCENT]: PERCENT_SIGN
} as Record<string, string>;

type ResidualsProps = {
    isLoading?: boolean;
};

const Residuals: FC<ResidualsProps> = ({ isLoading }) => {
    const dispatch = useDispatch();
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const isResidualUnitToggleEnabled = useIsExplicitLenderProgramGridActive();
    const [residualTypeByColumn, setResidualTypeByColumn] = useState<Record<number, ResidualValueType>>({});
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isPgOverrideIndicatorEnabled = useSelector(featureToggleSelectors.isPgOverrideIndicatorEnabled);
    const dealResidualType = useSelector(
        (state: StateTree) => offerSelectors.getResidualInfoOverrideWithFallback(state).baseResidualType
    );
    const [openingResidualType] = useState(() => (isExplicitLenderP1Enabled ? dealResidualType : undefined));
    const storedResiduals: ResidualsFromTerms = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridResiduals(state, undefined, openingResidualType)
    );
    const residuals: ResidualsFromTerms = useSelector((state: StateTree) =>
        offerReduxSelectors.getPaymentGridResiduals(state, residualTypeByColumn, openingResidualType)
    );
    const isCalculatingPayment = useSelector(offerReduxSelectors.isCalculatingPayment);
    // The column being edited keeps its unit: clearing it mid-edit remounts the input on the new unit's mask and
    // loses the typed text, or commits what was typed in the unit it was not typed in.
    const editingColumn = useRef<number | undefined>(undefined);
    const storedResidualTypes = storedResiduals.map(({ type }) => type);
    const storedResidualSignature = useSelector((state: StateTree) =>
        offerReduxSelectors
            .getPaymentGridResiduals(state)
            .map(({ month, type, val }) => `${month}:${type}:${val}`)
            .join('|')
    );
    const prevStoredResidualSignature = useRef(storedResidualSignature);
    useEffect(() => {
        const prevSignatures = prevStoredResidualSignature.current.split('|');
        const nextSignatures = storedResidualSignature.split('|');
        prevStoredResidualSignature.current = storedResidualSignature;
        setResidualTypeByColumn((current) => {
            if (!Object.keys(current).length) return current;
            const kept = editingColumn.current;
            const next: Record<number, ResidualValueType> = {};
            let dropped = false;
            Object.entries(current).forEach(([key, value]) => {
                const index = Number(key);
                if (
                    index === kept ||
                    prevSignatures[index] === nextSignatures[index] ||
                    nextSignatures[index]?.split(':')[1] === value
                ) {
                    next[index] = value;
                } else {
                    dropped = true;
                }
            });
            return dropped ? next : current;
        });
    }, [storedResidualSignature, isCalculatingPayment]);
    /**
     * Terms held by more than one column. Their inputs take a `-<column>` suffix so each is addressable; a term only
     * one column holds keeps the bare `residual-input-<term>` id every existing selector uses.
     */
    const duplicatedTerms = new Set(
        residuals.map(({ month }) => month).filter((month, index, all) => month != null && all.indexOf(month) !== index)
    );
    const dealXgId = useSelector(offerReduxSelectors.getDealXgId);
    const selectedTermMonths = useSelector(offerReduxSelectors.getSelectedTermMonths);
    const activeColumnIndex = useSelector(offerReduxSelectors.getResolvedPaymentGridSelectedColumnIndex);
    const overrideIndicator = usePaymentGridOverrideIndicator('residual');
    const paymentGridErrorState: IPaymentGridErrorState = useSelector(offerReduxSelectors.getPaymentGridErrorState);
    const columnView = useSelector(offerReduxSelectors.getPaymentGridColumnView);
    const userSuppliedResiduals = useSelector(offerReduxSelectors.getPaymentGridUserSuppliedResidualTerms, shallowEqual);
    const columnOverrides = useSelector(offerReduxSelectors.getPaymentGridColumnOverrides, shallowEqual);
    const isAspPgRedesignEnabled = useIsAspPaymentGridRedesignActive();
    const isPgOverrideIndicatorActive = isPgOverrideIndicatorEnabled && isAspPgRedesignEnabled;
    const syncsDealScreenIndicator = isExplicitLenderP1Enabled || isPgOverrideIndicatorActive;
    const displayedResidualTypes = residuals.map(({ type }) => type);
    const lastDisplayedResidualTypes = useRef(displayedResidualTypes);
    const lastActiveColumnIndex = useRef(activeColumnIndex);
    useEffect(() => {
        const previousActiveColumnIndex = lastActiveColumnIndex.current;
        lastActiveColumnIndex.current = activeColumnIndex;
        if (!isExplicitLenderP1Enabled || !isAspPgRedesignEnabled) return;
        if (activeColumnIndex == null || activeColumnIndex === previousActiveColumnIndex) return;
        const shownBeforeActive = lastDisplayedResidualTypes.current[activeColumnIndex];
        if (shownBeforeActive != null && shownBeforeActive !== dealResidualType) {
            dispatch(offerReduxActionCreators.updateResidualTabDisplayType(shownBeforeActive, false));
        }
    }, [activeColumnIndex]);
    useEffect(() => {
        lastDisplayedResidualTypes.current = displayedResidualTypes;
    });
    const isBordered = isResidualUnitToggleEnabled;

    const programBackedColumns = useSelector(offerReduxSelectors.getPaymentGridProgramBackedTerms, shallowEqual);
    const isLeaseRedesign = useIsAspPaymentGridRedesignActive() && offerType === LEASE;
    const columnIndicesAwaitingPair = useSelector(offerReduxSelectors.getPaymentGridColumnIndicesAwaitingPair, shallowEqual);
    const columnIndicesPairRejected = useSelector(offerReduxSelectors.getPaymentGridColumnIndicesPairRejected, shallowEqual);
    /**
     * Each residual input greys out only while its own column is in flight, as `SellRates` does. `isLoading` is the
     * pre-redesign grid-wide flag and stays the fallback, though this row only renders where it is always false.
     */
    const isColumnLoading = (month: number | null) =>
        columnView.isMasked ? month != null && columnView.loadingTerms.has(month) : Boolean(isLoading);

    const isColumnSettled = (month: number | null) => !isColumnLoading(month);
    const isColumnAwaitingPair = (month: number | null, index: number) =>
        isLeaseRedesign && month != null && isColumnSettled(month) && columnIndicesAwaitingPair.includes(index);
    const isColumnPairRejected = (month: number | null, index: number) =>
        isLeaseRedesign && month != null && isColumnSettled(month) && columnIndicesPairRejected.includes(index);
    const isResidualMissing = (item: ResidualItem, index: number) => isColumnAwaitingPair(item.month, index) && item.val == null;
    const isResidualFlagged = (item: ResidualItem, index: number) =>
        isResidualMissing(item, index) || isColumnPairRejected(item.month, index);
    const hasColumnMessage = residuals.some(isResidualFlagged);
    const isResidualRequired = (item: ResidualItem, index: number) => isResidualFlagged(item, index) && item.val == null;
    const hasResidualError = (item: ResidualItem, index: number) =>
        (areStringsEqual(paymentGridErrorState.lastUpdatedInput, `input_residual_${item.month}`) &&
            paymentGridErrorState.hasError &&
            !isLeaseRedesign) ||
        isResidualFlagged(item, index);
    const residualErrorMessage = (item: ResidualItem, index: number) =>
        isResidualRequired(item, index)
            ? PAYMENT_GRID_REQUIRED_INFO_MESSAGE
            : isColumnPairRejected(item.month, index)
            ? GENERIC_LAST_FIELD_CHANGED_ERROR
            : INPUT_INVALID_ERROR_MESSAGE;
    const showsMessageBelowUnitToggle = (item: ResidualItem, index: number) =>
        isResidualUnitToggleEnabled && item.month != null && hasResidualError(item, index);

    const isCalculatedColumn = useIsColumnCalculated();
    const isColumnCalculated = (month: number | null, index: number) => isCalculatedColumn(index, month, isColumnSettled(month));

    const handleUnitChange = (columnIndex: number, residualType: ResidualValueType) => {
        setResidualTypeByColumn((current) => ({ ...current, [columnIndex]: residualType }));
        dispatch(
            residualType === RESIDUAL_DOLLAR
                ? interactionAnalyticsActionCreators.baseResidualDollarsButton()
                : interactionAnalyticsActionCreators.baseResidualPercentButton()
        );
        if (isExplicitLenderP1Enabled && isAspPgRedesignEnabled && columnIndex === activeColumnIndex) {
            dispatch(offerReduxActionCreators.updateResidualTabDisplayType(residualType, false));
        }
    };

    const handleChange =
        (month: number | null, currentDisplayResidual?: number | null, unitType?: string, columnIndex?: number) =>
        (e: InterstateOnChangeEvent<TextInputEventValue>) => {
            const value = e.target.value === '' ? null : parseFloat(e.target.value);

            // Blanking the field is a removal, not an edit: every value `updateProgramResidualOverride` accepts is
            // a residual to price with, so this returned early, the override stayed in `overridesByTerm`, and the
            // cell repopulated from it on the next render with no way for the user to take it back (DE762937).
            //
            // Only a column that holds one of the user's own residuals. A cell showing the lender's figure has
            // nothing to remove, and dispatching there would re-price the column to deliver the value it already
            // shows.
            if (value == null) {
                // Nothing was showing, so nothing was cleared. A cell rendered blank because the column holds no
                // figure in the unit on display would otherwise read a bare focus-and-leave as a removal and throw
                // away the residual the user typed in the other unit.
                if (currentDisplayResidual == null) return;

                const ownEntry = columnOverrides[columnIndex as number];
                const ownsResidual = appliesToTerm(ownEntry, month) && ownEntry.residualInfo != null;
                if (typeof month === 'number' && (userSuppliedResiduals.includes(month) || ownsResidual)) {
                    if (isAspPgRedesignEnabled && columnIndex != null) {
                        dispatch(
                            offerReduxActionCreators.setPaymentGridColumnOverride(offerType, columnIndex, {
                                residualInfo: undefined
                            })
                        );
                    }
                    dispatch(
                        offerReduxActionCreators.removeProgramResidualOverride(month, true, {
                            isSelectedTerm: selectedTermMonths === month,
                            userIntent: CDL_USER_INTENT.PAYMENT_GRID_RESIDUAL,
                            ...(isAspPgRedesignEnabled && { gridColumnIndex: columnIndex })
                        })
                    );
                    if (syncsDealScreenIndicator && columnIndex === activeColumnIndex) {
                        dispatch(clearManualFieldOverrides(['baseResidual']));
                    }
                }
                return;
            }

            const isDollarUnit = unitType === RESIDUAL_DOLLAR;
            const committedUnit = isDollarUnit ? RESIDUAL_DOLLAR : RESIDUAL_PERCENT;
            // The unit is part of what is being committed, so accepting the figure the toggle showed is a real edit
            // even though it matches what is on screen - otherwise the only way to keep the unit is to retype.
            const residualUnitChanged = columnIndex != null && unitType !== storedResidualTypes[columnIndex];

            // A partial entry like "." or "-" parses to NaN, which neither equality check below catches on
            // its own — without this it would reach Payment Services as the term's residual.
            if (Number.isNaN(value)) return;
            if (currentDisplayResidual === value && !residualUnitChanged) return;

            logNewRelicPageAction('MV:PaymentGrid - Override residual', {
                mvOfferType: offerType,
                mvFieldEdited: 'mv:payment:grid:override-residual',
                mvResidualUnit: committedUnit,
                mvDealXgId: dealXgId
            });

            if (typeof month === 'number') {
                const isProgramBacked = programBackedColumns.includes(month);
                const valueChanged = value !== currentDisplayResidual;
                const ownEntry = columnOverrides[columnIndex as number];
                const keepsTypedResidual =
                    isPgOverrideIndicatorActive &&
                    appliesToTerm(ownEntry, month) &&
                    ownEntry.residualInfo != null &&
                    ownEntry.residualValueChanged === true;
                if (isAspPgRedesignEnabled && columnIndex != null) {
                    dispatch(
                        offerReduxActionCreators.setPaymentGridColumnOverride(offerType, columnIndex, {
                            residualInfo: {
                                // Unconditional: the type names which of `programResidual` / `programResidualPercent`
                                // `getResidualsFromTerms` reads back, and this write replaces `residualInfo` wholesale.
                                baseResidualType: committedUnit,
                                ...(isDollarUnit
                                    ? {
                                          programResidual: value,
                                          ...(!isProgramBacked && { baseResidualAmount: value })
                                      }
                                    : {
                                          programResidualPercent: value,
                                          ...(!isProgramBacked && { baseResidualPercent: value })
                                      })
                            },
                            ...(isExplicitLenderP1Enabled && { residualValueChanged: valueChanged || keepsTypedResidual })
                        })
                    );
                }

                dispatch(
                    offerReduxActionCreators.updateProgramResidualOverride(
                        { value, months: month, unitType: committedUnit },
                        true,
                        {
                            isSelectedTerm: selectedTermMonths === month,
                            // The grid cell is the residual this term should price with, so it drives the
                            // base residual too — otherwise the payment call reprices from the old value.
                            syncBaseResidual: !isProgramBacked,
                            clearBaseResidual: isProgramBacked,
                            residualUnitChanged,
                            userIntent: CDL_USER_INTENT.PAYMENT_GRID_RESIDUAL,
                            ...(isAspPgRedesignEnabled && { gridColumnIndex: columnIndex })
                        }
                    )
                );

                if (syncsDealScreenIndicator && columnIndex === activeColumnIndex && valueChanged) {
                    overrideIndicator.recordManualOverride();
                }
            }
        };

    const handleKeyDownEvent = (
        month: number | null,
        currentResidual: number | null,
        unitType: string | undefined,
        event: any,
        columnIndex: number
    ) => {
        if (event.code === ENTER_KEY_CODE) {
            const newTarget = getNewTarget(event);
            handleChange(month, currentResidual, unitType, columnIndex)(newTarget);
        }
    };

    return (
        <>
            <RowLabelCell
                label={RESIDUAL_LABEL}
                bordered={isBordered}
                cell={CompactGridItem}
                cellProps={{ $withUnitToggle: isResidualUnitToggleEnabled, $growToFit: hasColumnMessage }}
                baseHeight={isResidualUnitToggleEnabled ? '92px' : '56px'}
                unborderedMarginTop="0.4rem"
                testId="residual-label"
            />
            {residuals.map((item, index) => {
                const columnSuffix = duplicatedTerms.has(item.month) ? `-${index}` : '';
                const belowToggleMessageId = `residual-message-${item.month}-${index}`;

                return (
                    <CompactGridItem
                        xs={3}
                        className={' payment-grid-item'}
                        // Keyed on the column POSITION, which is unique by construction. The term is not: three
                        // columns holding one term gave every input the same key, and React reconciled them into an
                        // extra input box. Deliberately not the value either - that remounts the input on every
                        // reprice, dropping focus and the caret mid-edit.
                        key={`residual-${index}`}
                        paddingTop="0"
                        border={isBordered ? undefined : 'none'}
                        data-testid={`residual-${index}`}
                        $withUnitToggle={isResidualUnitToggleEnabled}
                        $isCalculated={isColumnCalculated(item.month, index)}
                        $growToFit={hasColumnMessage}
                    >
                        <PaymentGridOverrideIndicator
                            field="residual"
                            index={index}
                            enabled={overrideIndicator.enabled}
                            show={overrideIndicator.isShown(index)}
                            message={overrideIndicator.message}
                        >
                            <NumericInput
                                className="residual-input"
                                data-testid={`residual-input-${item.month}${columnSuffix}`}
                                name={'residual'}
                                value={`${item.val ?? ''}`}
                                label={item.type ? ResidualTypeMap[item.type] : ' '}
                                allowDecimal={true}
                                hasError={hasResidualError(item, index)}
                                maxValue={item.type === RESIDUAL_DOLLAR ? undefined : 100}
                                errorMessage={
                                    showsMessageBelowUnitToggle(item, index) ? undefined : residualErrorMessage(item, index)
                                }
                                slotProps={
                                    showsMessageBelowUnitToggle(item, index)
                                        ? {
                                              textInput: {
                                                  dataAttr: { 'aria-describedby': belowToggleMessageId } as unknown as {
                                                      [dataAttr: `data-${string}`]: string;
                                                  }
                                              }
                                          }
                                        : undefined
                                }
                                customErrorMessages={{ exceedsMaxValueRule: INPUT_INVALID_ERROR_MESSAGE }}
                                autoInsertCommas={item.type === RESIDUAL_DOLLAR}
                                // Match the deal screen's residual field exactly (ResidualField.interstate) so the
                                // grid shows the same number: 5 decimals allowed, none forced, so a value like
                                // 62.5 is not padded or rounded to a different precision than the field outside.
                                decimalMaxLength={item.type === RESIDUAL_DOLLAR ? 2 : 5}
                                decimalMinLength={0}
                                onFocus={() => {
                                    editingColumn.current = index;
                                }}
                                onBlur={(e) => {
                                    editingColumn.current = undefined;
                                    handleChange(item.month, item.val, item.type, index)(e);
                                }}
                                onKeyDown={(e) => handleKeyDownEvent(item.month, item.val, item.type, e, index)}
                                // Matches the sell rate row: only a column with no term at all is disabled. A blank
                                // cell is the normal state for a column the lender never priced — with Auto Select
                                // off, or on a term it quotes no program for — and that is exactly when the user
                                // needs to type a residual in (DE762937 issues 3 and 5).
                                disabled={!item.month}
                                sx={{
                                    ...(isColumnLoading(item.month) && disabledStyle),
                                    ...(isResidualRequired(item, index) && requiredInfoStyle),
                                    ...(showsMessageBelowUnitToggle(item, index) && hiddenInputMessageStyle)
                                }}
                                size="sm"
                            />
                        </PaymentGridOverrideIndicator>
                        {isResidualUnitToggleEnabled && item.month != null && (
                            // Greying lives on the wrapper and the handlers come off the options, as
                            // `ResidualTypeToggle` does: `ToggleGroup` forwards neither `disabled` nor `sx`, and
                            // dropping `onClick` is what makes a column in flight inert to pointer AND keyboard.
                            <StyledResidualUnitToggle
                                $disabled={isColumnLoading(item.month)}
                                aria-disabled={isColumnLoading(item.month) || undefined}
                                data-testid={`residual-unit-${item.month}${columnSuffix}`}
                            >
                                <ToggleGroup
                                    size="sm"
                                    toggleWidth="50%"
                                    id={`residual-unit-toggle-${item.month}${columnSuffix}`}
                                    data-testid={`residual-unit-toggle-${item.month}${columnSuffix}`}
                                    toggleButtonOptions={[
                                        {
                                            id: `residual-unit-percent-${item.month}${columnSuffix}`,
                                            value: PERCENT_SIGN,
                                            children: <span>{PERCENT_SIGN}</span>,
                                            selected: item.type !== RESIDUAL_DOLLAR,
                                            'aria-label': `Residual as a percent for the ${item.month} month term`,
                                            'data-testid': `residual-unit-percent-${item.month}${columnSuffix}`,
                                            ...(!isColumnLoading(item.month) && {
                                                onClick: () => handleUnitChange(index, RESIDUAL_PERCENT)
                                            })
                                        },
                                        {
                                            id: `residual-unit-dollar-${item.month}${columnSuffix}`,
                                            value: DOLLAR_SIGN,
                                            children: <span>{DOLLAR_SIGN}</span>,
                                            selected: item.type === RESIDUAL_DOLLAR,
                                            'aria-label': `Residual in dollars for the ${item.month} month term`,
                                            'data-testid': `residual-unit-dollar-${item.month}${columnSuffix}`,
                                            ...(!isColumnLoading(item.month) && {
                                                onClick: () => handleUnitChange(index, RESIDUAL_DOLLAR)
                                            })
                                        }
                                    ]}
                                />
                            </StyledResidualUnitToggle>
                        )}
                        {showsMessageBelowUnitToggle(item, index) && (
                            <Box sx={{ marginTop: '4px', ...(isResidualRequired(item, index) && requiredInfoStyle) }}>
                                <MessageBlock
                                    id={belowToggleMessageId}
                                    type="error"
                                    size="sm"
                                    message={residualErrorMessage(item, index)}
                                    data-testid={`residual-message-${item.month}${columnSuffix}`}
                                />
                            </Box>
                        )}
                    </CompactGridItem>
                );
            })}
        </>
    );
};

export default Residuals;
